import { liveReload } from '@shared/services/realtime.service';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { KpiStripComponent, ListKpi } from '@app/shared/components/kpi-strip/kpi-strip.component';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { ListView } from '@app/shared/utils/list-view';
import { currentMonthStart, currentMonthEnd, inDateRange } from '@app/shared/utils/date-range';
import { UserScopeService, UserScope, inScope } from '@app/shared/services/user-scope.service';
import { ModulesService } from '@shared/services/modules.service';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { SimpleExcelService } from '@app/shared/services/simple-excel.service';

import { CustodyService, LogisticsCatalogs, fmtDate, statusChip, errMsg } from '../logistics.service';
import { CustodyFormDialogComponent, CustodyType } from './custody-form-dialog.component';
import { CustodyDetailDialogComponent } from './custody-detail-dialog.component';
import { PickDialogComponent } from '../pick-dialog.component';

/**
 * Resguardo y devolución extraordinaria de insumos (RES-n / DEV-n).
 * Sin autorización del in-house: cada registro avisa al coordinador del
 * hospital y a mesa de control.
 */
@Component({
  selector: 'app-custody',
  standalone: true,
  imports: [NgTemplateOutlet, FormsModule, MatTableModule, MatPaginatorModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatButtonModule, MatIconModule, MatTooltipModule, MatMenuModule, MatButtonToggleModule, MatDatepickerModule, MatDialogModule,
    KpiStripComponent, PageLoaderComponent, PageTitleComponent, SearchPillComponent, FilterChipsComponent],
  templateUrl: './custody.component.html',
  styleUrl: '../logistics.scss',
})
export class CustodyComponent implements OnInit {
  private readonly modules = inject(ModulesService);
  readonly title = this.modules.moduleTitle('/custody', 'Resguardo y devolución de material');

  readonly cat = inject(LogisticsCatalogs);
  private readonly service = inject(CustodyService);
  private readonly dialog = inject(MatDialog);
  private readonly msg = inject(CustomMessageService);
  private readonly excel = inject(SimpleExcelService);
  private readonly scopeService = inject(UserScopeService);
  private scope: UserScope = { places: null, specialties: null };

  readonly types = [{ id: 'RESGUARDO', name: 'Resguardo' }, { id: 'DEVOLUCION', name: 'Devolución extraordinaria' }];
  readonly statuses = ['Abierto', 'Devuelto', 'Reportado', 'Cerrado'];
  readonly fmtDate = fmtDate;
  readonly statusChip = statusChip;
  readonly listView = new ListView('custody', 'table');
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(['custody'], () => this.pageLoad.ready && this.pageLoad.refresh(this.load()));

  periodStart: Date | null = currentMonthStart();
  periodEnd: Date | null = currentMonthEnd();
  typeFilter = '';
  statusFilter = '';
  placeFilter = '';
  searchText = '';

  columns = ['folio', 'type', 'createdOn', 'place', 'warehouse', 'responsible', 'commitmentDate', 'pieces', 'status', 'actions'];
  dataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private allRows: any[] = [];
  kpis: ListKpi[] = [];
  activeKpi: string | null = null;
  private static readonly KPI: Record<string, (r: any) => boolean> = {
    open: r => r.type === 'RESGUARDO' && r.status === 'Abierto',
    returns: r => r.type === 'DEVOLUCION' && r.status === 'Reportado',
    untagged: r => r._untagged > 0,
    other: r => r._other > 0,
  };

  get placeOptions(): any[] { return this.cat.places.filter(p => inScope(this.scope.places, p.id, false)); }

  ngOnInit(): void { void this.init(); }
  init(): Promise<boolean> { return this.pageLoad.run(() => this.initialLoad()); }

  private async initialLoad(): Promise<void> {
    try { this.scope = await this.scopeService.load(); } catch { /* sin alcance */ }
    await this.cat.load();
    await this.load();
  }

  async load(): Promise<void> {
    try {
      const condition: any = { deleted: false };
      if (this.typeFilter) condition.type = this.typeFilter;
      if (this.placeFilter) condition.place = this.placeFilter;
      const r: any = await this.service.search({ searchType: 'default', condition });
      this.allRows = ((r?.data ?? []) as any[])
        .filter(x => inScope(this.scope.places, x.place))
        .filter(x => !this.statusFilter || x.status === this.statusFilter)
        .filter(x => inDateRange(x.createdOn, this.periodStart, this.periodEnd))
        .map(x => {
          const items: any[] = x.items ?? [];
          const untagged = x.type === 'DEVOLUCION' ? items.filter(i => !i.tagged).length : 0;
          const other = items.filter(i => i.otherAccount).length;
          return {
            ...x, _place: this.cat.placeName(x.place),
            _warehouse: this.cat.warehousesById[x.warehouse]?.name ?? '',
            _responsible: x.type === 'RESGUARDO'
              ? `${x.responsibleName ?? ''}${x.responsibleArea ? ' · ' + x.responsibleArea : ''}` : '',
            _commitment: fmtDay(x.commitmentDate),
            _overdue: x.type === 'RESGUARDO' && x.status === 'Abierto' && !!x.commitmentDate && String(x.commitmentDate).slice(0, 10) < localDay(new Date()),
            _pieces: items.reduce((a, i) => a + (Number(i.quantity) || 1), 0), _untagged: untagged, _other: other,
            _detail: `${items.filter(i => i.tagged !== false && (x.type === 'RESGUARDO' ? !!i.stock : i.tagged)).length} etiquetado(s) · ${items.filter(i => x.type === 'RESGUARDO' ? !i.stock : !i.tagged).length} sin etiqueta`,
            _codes: items.map(i => `${i.code ?? ''} ${i.tagRfid ?? ''} ${i.foundFolio ?? ''}`).join(' '),
          };
        })
        .sort((a, b) => String(b.createdOn ?? '').localeCompare(String(a.createdOn ?? '')));
      this.dataSource.filterPredicate = (row, f) => [row.folio, row._place, row._warehouse, row._responsible, row._commitment, row._codes, row.status].join(' ').toLowerCase().includes(f);
      this.refreshKpis();
      this.dataSource.paginator = this.paginator;
      this.dataSource.filter = this.searchText.trim().toLowerCase();
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudieron cargar los registros.'), dismissBtn: true });
    }
  }

  private refreshKpis(): void {
    const c = (k: string) => this.allRows.filter(CustodyComponent.KPI[k]).length;
    this.kpis = [
      { key: 'total', label: 'Registros', icon: 'inventory', value: this.allRows.length, isTotal: true },
      { key: 'open', label: 'Resguardos abiertos', icon: 'shield', value: c('open'), tone: 'warn' },
      { key: 'returns', label: 'Devoluciones por cerrar', icon: 'assignment_return', value: c('returns') },
      { key: 'untagged', label: 'Con piezas sin etiqueta', icon: 'label_off', value: c('untagged'), tone: 'danger' },
      { key: 'other', label: 'De otra cuenta', icon: 'compare_arrows', value: c('other'), tone: 'danger' },
    ];
    this.applyKpi();
  }
  setKpi(k: string | null): void { this.activeKpi = k; this.applyKpi(); }
  private applyKpi(): void {
    const f = this.activeKpi ? CustodyComponent.KPI[this.activeKpi] : null;
    this.dataSource.data = f ? this.allRows.filter(f) : this.allRows;
  }

  applySearch(v: string): void { this.searchText = v ?? ''; this.dataSource.filter = this.searchText.trim().toLowerCase(); }

  typeName(t: string): string { return t === 'RESGUARDO' ? 'Resguardo' : 'Devolución'; }

  get filterChips(): FilterChip[] {
    const c: FilterChip[] = [];
    if (this.typeFilter) c.push({ key: 'type', label: 'Tipo', value: this.typeName(this.typeFilter) });
    if (this.placeFilter) c.push({ key: 'place', label: 'Hospital', value: this.cat.placeName(this.placeFilter) });
    if (this.statusFilter) c.push({ key: 'status', label: 'Estado', value: this.statusFilter });
    if (this.periodStart || this.periodEnd) c.push({ key: 'period', label: 'Periodo', value: `${fmtDate(this.periodStart, false) || '…'} – ${fmtDate(this.periodEnd, false) || '…'}`, removable: false });
    return c;
  }
  async removeChip(k: string): Promise<void> {
    if (k === 'type') this.typeFilter = '';
    if (k === 'place') this.placeFilter = '';
    if (k === 'status') this.statusFilter = '';
    await this.load();
  }
  async clearFilters(): Promise<void> {
    this.typeFilter = ''; this.placeFilter = ''; this.statusFilter = '';
    this.periodStart = currentMonthStart(); this.periodEnd = currentMonthEnd();
    this.applySearch('');
    await this.load();
  }

  openForm(type: CustodyType): void {
    this.dialog.open(CustodyFormDialogComponent, {
      data: { type, scopePlaces: this.scope.places }, width: '900px', maxWidth: '96vw', maxHeight: '92vh', autoFocus: 'first-tabbable',
    }).afterClosed().subscribe(ok => { if (ok) void this.load(); });
  }

  view(c: any): void {
    this.dialog.open(CustodyDetailDialogComponent, { data: { record: c }, maxWidth: '95vw', maxHeight: '92vh' });
  }

  /** Resguardo: devolver (opcionalmente a otro locker). Devolución: reincorporar las etiquetadas y cerrar. */
  giveBack(c: any): void {
    const resg = c.type === 'RESGUARDO';
    const tagged = (c.items ?? []).filter((i: any) => i.tagged && i.stock).length;
    if (!resg && !tagged) { void this.close(c, null); return; }
    this.dialog.open(PickDialogComponent, { data: {
      title: resg ? `Devolver resguardo ${c.folio}` : `Cerrar devolución ${c.folio}`,
      message: resg
        ? 'Las piezas vuelven a <b>Disponible</b>. Si regresan a otro locker, elígelo; si no, se quedan donde estaban.'
        : `Las <b>${tagged}</b> pieza(s) etiquetadas se reincorporan como Disponible en el locker que elijas. Las que no tienen etiqueta deben darse de alta por Recepción de material.`,
      mode: 'warehouse', label: 'Locker / in-house', value: resg ? null : c.warehouse, places: null,
      required: !resg, emptyLabel: '— El mismo de donde salieron —', acceptLabel: resg ? 'Devolver' : 'Reincorporar y cerrar',
    } }).afterClosed().subscribe((wh: string | null | undefined) => {
      if (wh === undefined) return;
      void this.close(c, wh);
    });
  }

  private async close(c: any, warehouse: string | null): Promise<void> {
    try {
      const r = await this.service.giveBack(c.id, warehouse);
      await this.msg.open({ title: '¡Listo!', type: 'success', duration: 2500,
        message: `<b>${c.folio}</b> ${r?.status === 'Devuelto' ? 'devuelto' : 'cerrado'}: ${r?.pieces ?? 0} pieza(s) disponibles.` });
      await this.load();
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudo completar.'), dismissBtn: true });
    }
  }

  async downloadExcel(): Promise<void> {
    const data: any[] = [];
    for (const c of (this.dataSource.filteredData ?? []) as any[]) for (const i of (c.items ?? [])) data.push({
      folio: c.folio, type: this.typeName(c.type), date: fmtDate(c.createdOn), place: c._place, warehouse: c._warehouse,
      responsible: c._responsible, commitment: c._commitment, tagged: c.type === 'RESGUARDO' || i.tagged ? 'Sí' : 'No',
      code: i.code ?? '', description: i.description ?? '', batch: i.batch ?? '', qty: i.quantity ?? 1, tag: i.tagRfid ?? '',
      folioOut: i.foundFolio ?? '', other: i.otherAccount ? (i.foundPlaceName || 'Sí') : '', status: c.status,
    });
    const cols = [['folio', 'Folio'], ['type', 'Tipo'], ['date', 'Fecha'], ['place', 'Alias'], ['warehouse', 'Locker'], ['responsible', 'Responsable'], ['commitment', 'Fecha compromiso de retiro'],
      ['tagged', 'Con etiqueta'], ['code', 'Código'], ['description', 'Descripción'], ['batch', 'Lote'], ['qty', 'Piezas'],
      ['tag', 'Etiqueta'], ['folioOut', 'Folio en que salió'], ['other', 'Otra cuenta'], ['status', 'Estado']]
      .map(([k, n]) => ({ columnKey: k, columnName: n, type: 'string' as const }));
    await this.excel.simpleXLSX({ fileName: `Resguardos y devoluciones ${new Date().toISOString().slice(0, 10)}`,
      sheets: [{ sheetName: 'Resguardo-Devolución', columns: cols as any, data, header: [], lineBreak: 0 }] });
  }
}

/** '2026-10-20' → '20/10/2026' sin pasar por Date (evita el corrimiento por zona horaria). */
function fmtDay(value: any): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

function localDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
